
import React, { useState, useEffect, useRef } from 'react';
import { ProductCard } from './ProductCard';
import { Product } from '../types';

interface ProductCardLoaderProps {
    product: Product;
    isDark: boolean;
    onOpenDetails: (product: Product) => void;
    onAddToCart: (e: React.MouseEvent, product: Product) => void;
    isAdded: boolean;
    priority?: boolean;
    isRow?: boolean;
}

export const ProductCardLoader: React.FC<ProductCardLoaderProps> = ({
    product,
    isDark,
    onOpenDetails,
    onAddToCart,
    isAdded,
    priority = false,
    isRow = false
}) => {
    const [shouldRender, setShouldRender] = useState(priority); // Immediate render for priority items
    const itemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (priority) return; // Skip observer for priority items

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setShouldRender(true);
                observer.disconnect();
            }
        }, {
            rootMargin: '400px', // Preload buffer
        });
        if (itemRef.current) observer.observe(itemRef.current);
        return () => observer.disconnect();
    }, [priority]);

    const wrapperClass = isRow
        ? "min-w-[160px] w-[160px] md:min-w-[220px] md:w-[220px] h-full"
        : "w-full h-full min-h-[300px]";

    return (
        <div ref={itemRef} className={wrapperClass}>
            {shouldRender ? (
                <ProductCard
                    product={product}
                    isDark={isDark}
                    onOpenDetails={onOpenDetails}
                    onAddToCart={onAddToCart}
                    isAdded={isAdded}
                    priority={priority}
                    isRow={isRow}
                />
            ) : (
                <div className={`w-full h-full rounded-[2rem] border animate-pulse ${isDark ? 'bg-white/5 border-white/5' : 'bg-slate-100 border-slate-200'}`} />
            )}
        </div>
    );
};
